// 1. React and core hooks
import { useCallback } from 'react';

// 2. External libraries
import { useMutation } from '@tanstack/react-query';

// 4. Type imports
import type { OAuthAuthorizeRequest } from '~/generated/api/model';

// 5. Custom hooks and utilities
import { authorizePostV1OauthAuthorizePost } from '~/generated/api/oauth/oauth';

/**
 * Backend authorization response shape.
 *
 * The generated client types the response as `any`, so we narrow it with a
 * local type guard before reading `redirect_url`.
 */
interface OAuthAuthorizeResponseBody {
  redirect_url: string;
}

function hasRedirectUrl(value: unknown): value is OAuthAuthorizeResponseBody {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { redirect_url?: unknown }).redirect_url === 'string'
  );
}

/**
 * OAuth authorization request parameters.
 *
 * Matches backend OAuthAuthorizeRequest schema with camelCase naming.
 * Explicit type annotations for all properties.
 */
export interface OAuthAuthorizeParams {
  /** OAuth client identifier */
  clientId: string;
  /** OAuth callback URL */
  redirectUri: string;
  /** Selected topic UUID for OAuth connection */
  topicId: string;
  /** Requested OAuth scopes (empty array for no scopes) */
  scopes: string[];
  /** CSRF protection token (optional) */
  state?: string;
  /** PKCE code challenge (optional) */
  codeChallenge?: string;
  /** PKCE challenge method: "S256" only (backend rejects "plain") (optional) */
  codeChallengeMethod?: 'S256';
}

/**
 * OAuth authorization response from backend.
 *
 * Backend returns 302 redirect, but we handle client-side redirect.
 * Explicit type annotations for all properties.
 */
export interface OAuthAuthorizeResult {
  /** Authorization code for token exchange */
  code: string;
  /** Original state parameter for CSRF protection */
  state?: string;
}

/**
 * Hook for OAuth authorization flow.
 *
 * Submits POST /oauth/authorize with topic selection and permissions.
 * On success, redirects to client's redirect_uri with authorization code.
 * On error, returns error object for display in ErrorAlert.
 *
 * Per §TS.5.1: Full generic type parameters for TanStack Query mutation.
 * Per §TS.3.1: Function declaration for named export.
 *
 * @returns UseMutationResult with mutate, mutateAsync, isPending, error, reset
 *
 * @example
 * ```tsx
 * const { authorize, isLoading, error } = useOAuthAuthorize();
 *
 * const handleAuthorize = async () => {
 *   await authorize({
 *     clientId: 'my-client',
 *     redirectUri: 'https://client.example.com/callback',
 *     topicId: 'abc-123',
 *     scopes: ['read', 'write'],
 *     state: 'csrf-token',
 *   });
 *   // Redirects to redirect_uri on success
 * };
 * ```
 */
export function useOAuthAuthorize(): {
  authorize: (params: OAuthAuthorizeParams) => Promise<void>;
  isLoading: boolean;
  error: Error | null;
  reset: () => void;
} {
  const mutation = useMutation<string, Error, OAuthAuthorizeParams, unknown>({
    mutationFn: async (params: OAuthAuthorizeParams) => {
      // The shared transport injects the Bearer JWT via its request interceptor
      // and rejects on any non-2xx response, so no manual token handling is needed.
      const payload: OAuthAuthorizeRequest = {
        clientId: params.clientId,
        redirectUri: params.redirectUri,
        // Send null instead of empty string when no topic selected
        topicId: params.topicId || null,
        scopes: params.scopes,
        state: params.state,
        codeChallenge: params.codeChallenge,
        // The string-literal union shares values with the generated enum.
        codeChallengeMethod: params.codeChallengeMethod,
      };

      // Response is typed as `unknown` in the generated client; narrow it before use.
      const data: unknown = await authorizePostV1OauthAuthorizePost(payload);
      if (hasRedirectUrl(data)) {
        return data.redirect_url;
      }

      throw new Error('Authorization failed: No redirect URL in response');
    },
    retry: 0, // No retry for auth errors (403/404/400 indicate invalid request)
    onSuccess: (redirectUrl: string) => {
      // Backend has already built the redirect URL with code and state
      // Just navigate to it
      window.location.href = redirectUrl;
    },
  });

  // Per §TS.13.1: Ensure callback stability with useCallback
  const authorize = useCallback(
    async (params: OAuthAuthorizeParams) => {
      await mutation.mutateAsync(params);
    },
    [mutation]
  );

  return {
    authorize,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}
